import React, { useEffect, useState } from 'react';
import { X, Clock, CheckCircle, XCircle, Undo2, FileSignature } from 'lucide-react';
import { getReviewHistory } from '../../services/reviewWorkflowApi';

/**
 * Read-only attestation history for one reviewable entity (FECOA-1611).
 *
 * Renders the append-only ReviewSubmission records as a timeline: who submitted,
 * who decided, the decision + reason, and the 21 CFR Part 11 e-signature block
 * (meaning / signed-by / signed-at). Shown in a modal triggered from the review
 * actions or the queue.
 */
const STATUS_META = {
  pending: { label: 'Pending review', icon: Clock, cls: 'text-blue-600 bg-blue-50 border-blue-200' },
  approved: { label: 'Approved', icon: CheckCircle, cls: 'text-teal-700 bg-teal-50 border-teal-200' },
  denied: { label: 'Denied', icon: XCircle, cls: 'text-red-600 bg-red-50 border-red-200' },
  withdrawn: { label: 'Withdrawn', icon: Undo2, cls: 'text-gray-600 bg-gray-50 border-gray-200' },
};

const fmt = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

// Plain-English explanation of the entity_version hash, shown on hover.
const VERSION_TOOLTIP =
  'Unique system identifier for the exact content that was reviewed. '
  + 'If you ever need to trace this approval, or flag it as made in error, '
  + 'share this ID with support and they can pull up the full record.';

const ReviewHistory = ({ entityType = 'assessment', objectId, entityName, onClose }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getReviewHistory(entityType, objectId);
        if (active) setRows(Array.isArray(data) ? data : data.results || []);
      } catch {
        if (active) setError('Could not load review history.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [entityType, objectId]);

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 dark:bg-black/70 flex items-center justify-center z-50 p-4 text-left whitespace-normal"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Review history</h3>
            {entityName && <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{entityName}</p>}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 overflow-y-auto">
          {loading && <div className="text-center py-10 text-gray-400 text-sm">Loading history…</div>}
          {error && <div className="text-sm text-red-600 py-4">{error}</div>}
          {!loading && !error && rows.length === 0 && (
            <div className="text-center py-10 text-gray-500 text-sm">
              No review activity yet for this item.
            </div>
          )}

          {!loading && rows.length > 0 && (
            <ol className="relative border-l border-gray-200 dark:border-gray-700 ml-2 space-y-6">
              {rows.map((r) => {
                const meta = STATUS_META[r.status] || STATUS_META.pending;
                const Icon = meta.icon;
                return (
                  <li key={r.id} className="ml-5">
                    <span className={`absolute -left-3 flex items-center justify-center w-6 h-6 rounded-full border ${meta.cls}`}>
                      <Icon className="w-3.5 h-3.5" />
                    </span>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${meta.cls}`}>
                        {meta.label}
                      </span>
                      {r.entity_version && (
                        <span className="inline-flex items-center gap-1 cursor-help" title={VERSION_TOOLTIP}>
                          <span className="text-[11px] text-gray-500 dark:text-gray-400">Reviewed version</span>
                          <span className="text-[11px] font-mono text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-full px-1.5 py-0.5">
                            {String(r.entity_version).slice(0, 8)}
                          </span>
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-700 dark:text-gray-300">
                      Submitted by <span className="font-medium">{r.submitted_by_name || 'Unknown'}</span>
                      <span className="text-gray-400"> · {fmt(r.submitted_at)}</span>
                    </p>
                    {r.is_decided && (
                      <p className="text-sm text-gray-700 dark:text-gray-300 mt-0.5">
                        {/* Prefer the frozen signature name so the record stays
                            consistent as-of the decision, even if the approver
                            later renames their profile. */}
                        Decided by <span className="font-medium">{r.signed_by_name || r.decided_by_name || 'Unknown'}</span>
                        <span className="text-gray-400"> · {fmt(r.decided_at)}</span>
                      </p>
                    )}
                    {r.decision_reason && (
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 bg-gray-50 dark:bg-gray-900 border border-gray-100 dark:border-gray-700 rounded-md px-3 py-2">
                        “{r.decision_reason}”
                      </p>
                    )}
                    {r.signed_by_name && (
                      <p className="text-xs text-gray-500 mt-1.5 flex items-center gap-1">
                        <FileSignature className="w-3.5 h-3.5" />
                        {r.signature_meaning || 'Signed'} — {r.signed_by_name}
                        <span className="text-gray-400"> · {fmt(r.signed_at)}</span>
                      </p>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewHistory;
